import React from 'react';
import { ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import Header from '../../components/Navbar/Header';
import Footer from '../../components/Footer';
import Hero from './../../assets/vision/Hero.jpg';
import { Col, Container, Row } from 'react-bootstrap'
import { Link } from 'react-router-dom';
import { CountUpStats } from './components/CountUpStats';
import { OutlineCards } from './components/OutlineCards';

// Button component
const Button = ({ children, size = 'default', variant = 'default', className = '', ...props }) => {
  const sizeClasses = {
    default: 'h-10 px-4 py-2',
    sm: 'h-9 rounded-md px-3',
    lg: 'h-11 rounded-md px-8',
    icon: 'h-10 w-10',
  };

  const variantClasses = {
    default: 'bg-black text-white hover:bg-black/90',
    outline: 'border border-black bg-white hover:bg-gray-100 text-black',
    secondary: 'bg-gray-200 text-black hover:bg-gray-300',
    ghost: 'hover:bg-gray-100 text-black',
    link: 'text-black underline-offset-4 hover:underline',
  };

  return (
    <button
      className={`inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 ${sizeClasses[size]} ${variantClasses[variant]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

// StatsCard component
const StatsCard = ({ number, label, delay = 0 }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5 }}
    >
      <div className="relative overflow-hidden rounded-lg border bg-white text-black shadow-sm">
        <div className="p-6">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-50" />
          <div className="relative">
            <h3 className="text-4xl font-bold mb-2 text-black">{number}</h3>
            <p className="text-sm text-gray-600">{label}</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default function Vision() {
  return (
    <div className="min-h-screen bg-white text-black">
      <Header
       
      />

      {/* Hero Section */}
      <section
        className="pt-32 pb-16 bg-cover bg-center text-white"
        style={{
          backgroundImage: `url(${Hero})`,
        }}
      >
        <div className="container">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h1 className="text-4xl font-bold tracking-tighter sm:text-5xl md:text-6xl lg:text-7xl mb-6">
              Revolutionizing Small Business Growth
            </h1>
            <p className="text-xl mb-8">
              FuteurAI combines cutting-edge AI with a bold mission: to empower every small business in America to thrive, creating jobs, driving innovation, and unlocking $1 trillion in economic value.
            </p>
            <a 
  href="https://app.futeur.ai/signup" 
  target="_blank" 
  rel="noopener noreferrer"
  className="inline-flex items-center justify-center px-6 py-3 text-lg font-medium text-white bg-black rounded-lg shadow hover:bg-black group transition-all"
>
  Join the Revolution
  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transform transition-transform duration-300" />
</a>

          </div>
        </div>
      </section>

      {/* Live Stats Section */}
      <CountUpStats />

      {/* Impact Section */}
      <section className="py-16">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl mb-6">
              The Current Reality
            </h2>
            <p className="text-lg text-gray-600">
              Small businesses are the backbone of the U.S. economy, but they're under strain.
            </p>
          
          </div>
        </div>
      </section>

      <section className='py-2'>
      <div className="xs:px-2">
          <div className="w-full px-[0.5rem] xx:px-[1.5rem] xs:px-[2.5rem] py-[1.5rem] xx:py-[2.5rem] xs:py-[4rem] rounded-[56px]">
              <Container>

                  <Row className='gap-y-4'>
                      <Col md={6} lg={4}>
                          <div className="w-full h-[300px] xl:h-[400px] p-4 rounded-[24px] bg-Mneutral50 flex flex-wrap">
                          <h5 className='font-bold text__24 w-full text-black text-center'>
                          SMEs, Jobs, Credit Challenges</h5>
                              <h5 className='font-medium text__24 w-full text-black'>
                              34.8 million SMEs support over 61 million jobs, yet struggle with credit access and growth barriers.</h5>

                              <div className="self-end">
                                  <h3 className='font-medium text__72 mb-2 text-black'></h3>
                                  <div className="self-end">
                                </div>
                              </div>
                          </div>
                      </Col>
                      <Col md={6} lg={4}>
                          <div className="w-full h-[300px] xl:h-[400px] p-4 rounded-[24px] bg-Mneutral50 flex flex-wrap">
                          <h5 className='font-bold text__24 w-full text-black text-center'>
                          Fragmented Tools</h5>
                              <h5 className='font-medium text__24 w-full text-black'>  Businesses face inefficiencies, relying on outdated credit systems.</h5>

                              <div className="self-end">
                              <div className="self-end">
                                  
                                </div>
      
                              </div>
                          </div>
                      </Col>
                      <Col md={6} lg={4}>
                          <div className="w-full h-[300px] xl:h-[400px] p-4 rounded-[24px] bg-Mneutral50 flex flex-wrap">
                          <h5 className='font-bold text__24 w-full text-black text-center'>
                          Inequality in Financial Access</h5>
                              <h5 className='font-medium text__24 w-full text-black'> Minority and women-owned businesses face systemic barriers, limiting their potential.</h5>

                              <div className="self-end">
                             
                            </div>
                          </div>
                      </Col>
                  </Row>

              </Container>
          </div>
      </div>
  </section>
       {/* Why FuteurAI Section */}
       <section className="py-16 bg-gray-50">
       <div className="container">
         <div className="max-w-3xl mx-auto text-center">
           <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl mb-6">
             Why FuteurAI?
           </h2>
         </div>
       </div>
     </section>
     
     <section className='py-2'>
     <div className="xs:px-2">
         <div className="w-full px-[0.5rem] xx:px-[1.5rem] xs:px-[2.5rem] py-[1.5rem] xx:py-[2.5rem] xs:py-[4rem] rounded-[56px]">
             <Container>

                 <Row className='gap-y-4'>
                     <Col md={6} lg={4}>
                         <div className="w-full h-[300px] xl:h-[400px] p-4 rounded-[24px] bg-Mneutral50 flex flex-wrap">
                             <h5 className='font-bold text__24 w-full text-black mb-10 text-center'>
                             AI That Delivers</h5>
                             <h5 className='font-medium text__24 w-full text-black'>
                             Real-time credit insights with unmatched accuracy and speed. Predictive analytics to forecast market trends and prepare businesses for the future.</h5>

                             <div className="self-end">
                                 <h3 className='font-medium text__72 mb-2 text-black'></h3>
                                 <div className="self-end">
                               </div>
                             </div>
                         </div>
                     </Col>
                     <Col md={6} lg={4}>
                     <div className="w-full h-[300px] xl:h-[400px] p-4 rounded-[24px] bg-Mneutral50 flex flex-wrap">
                         <h5 className='font-bold text__24 w-full text-black text-center'>
                         Inclusive Approach</h5>
                         <h5 className='font-medium text__24 w-full text-black'>
                         Breaking barriers for minority and women-owned businesses, fostering equity and innovation.</h5>

                         <div className="self-end">
                             <h3 className='font-medium text__72 mb-2 text-black'></h3>
                             <div className="self-end">
                           </div>
                         </div>
                     </div>
                 </Col>
                 <Col md={6} lg={4}>
                 <div className="w-full h-[300px] xl:h-[400px] p-4 rounded-[24px] bg-Mneutral50 flex flex-wrap">
                     <h5 className='font-bold text__24 w-full text-black text-center'>
                     Impact at Scale</h5>
                     <h5 className='font-medium text__24 w-full text-black'>
                     34.8 million businesses already supported, with 59.8 million projected by 2029.</h5>

                     <div className="self-end">
                         <h3 className='font-medium text__72 mb-2 text-black'></h3>
                         <div className="self-end">
                       </div>
                     </div>
                 </div>
             </Col>
                 </Row>

             </Container>
         </div>
     </div>
 </section>
  <OutlineCards />

      {/* CTA Section */}
      <section className="py-16 bg-gray-100">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl text-black mb-6">
              Be Part of the Revolution
            </h2>
            <p className="text-lg text-gray-600 mb-8 font-bold">
              At FuteurAI, we are not just building tools—we're empowering potential, unlocking opportunities, and shaping the future of small business success. Rooted in resilience and innovation, we transform credit literacy into action, equipping businesses to grow, lead, and thrive. By amplifying voices and bridging financial gaps, we ensure that every entrepreneur controls their narrative and secures their place in a thriving economy.
            </p>
            <div className="flex justify-center space-x-4">
            <Button 
          size="lg" 
          variant="default" 
          className="group" 
          onClick={() => {
            window.location.href = 'https://app.futeur.ai/signup';
            setTimeout(() => window.scrollTo(0, 0), 0); // Ensure the scroll happens after navigation
          }}
        >
         Join the Moment
          <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
        </Button>
          <Button 
          size="lg" 
          variant="outline" 
          className="group" 
          onClick={() => {
            window.location.href = '/about-futeur';
            setTimeout(() => window.scrollTo(0, 0), 0); // Ensure the scroll happens after navigation
          }}
        >
          Learn More About FuteurAI
          <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
        </Button>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

